const Q = require('q');
const _ = require('underscore');
const model = require('model/model');
const analyticsService = require('services/analyticsService');
const { get, set } = require('model/stores/indexedDbStore');

let offlineAvailable = 'serviceWorker' in navigator && 'indexedDB' in window;
let queue = [];
let timerId = null;
let tracker = {};
let settingOfflineEvents = false;
let deferred;

const resolveDeferred = err => {
	if (!deferred) {
		return;
	}

	if (err) {
		deferred.reject(err);
	} else {
		deferred.resolve();
	}

	deferred = undefined;
};

const error = (code, err) => {
	return analyticsService.error({
		code: code,
		error: err
	});
};

const getIDBOffset = ts => {
	return get('eventTimestampOffset')
		.then(eventTimestampOffset => {
			let prms = null;
			if (typeof eventTimestampOffset !== 'number' || Number.isNaN(eventTimestampOffset)) {
				//Offset hasn't been stored
				prms = Q(ts);
			} else {
				model.set('userSessionEventOffset', eventTimestampOffset);
				prms = Q(ts - eventTimestampOffset);
			}
			return prms;
		})
		.catch(e => {
			return Q(ts);
		});
};

const fetchRemoteTimestamp = ts => {
	return analyticsService
		.timestamp()
		.then(response => {
			if (response && response.time) {
				let timestamp = response.time;
				let eventTimestampOffset = Date.now() - timestamp;
				if (eventTimestampOffset < 60 * 1000) {
					eventTimestampOffset = 0;
				}

				model.set('userSessionEventOffset', eventTimestampOffset);
				let prms = Q();
				if (offlineAvailable) {
					prms = set('eventTimestampOffset', eventTimestampOffset);
				}
				return prms.then(() => ts - eventTimestampOffset);
			}
			if (offlineAvailable) {
				return getIDBOffset(ts);
			}

			return Q(ts);
		})
		.catch(e => {
			if (offlineAvailable) {
				return getIDBOffset(ts);
			}
			throw e;
		});
};

const getTimestamp = () => {
	let ts = Date.now();
	let tsPrms = Q();
	let offset = model.get('userSessionEventOffset');
	if (offset !== null && typeof offset === 'number' && !Number.isNaN(offset)) {
		return Q(ts - offset);
	}

	if (offlineAvailable && model.get('offlineMode')) {
		tsPrms = getIDBOffset(ts);
	} else {
		tsPrms = fetchRemoteTimestamp(ts);
	}
	return tsPrms.catch(() => ts);
};

function checkForDuplicates() {
	let eventKeys = tracker ? Object.keys(tracker) : null;
	let duplicatesFound = false;

	if (eventKeys && eventKeys.length) {
		eventKeys.forEach(key => {
			let allUniqueEvents = true;
			let eventTokens = tracker[key];
			// Check to see if we have multiples of a single event type
			if (eventTokens) {
				let uniqueEvents = Object.keys(eventTokens);
				// Check that each event for a key is unique
				allUniqueEvents = uniqueEvents.every((e, idx) => {
					// Check every following event for duplicity
					if (eventTokens[e] > 1) {
						return false;
					}
					// Found no duplicates of current event 'e'
					return true;
				});
			}
			if (!allUniqueEvents) {
				duplicatesFound = true;
				error('DUPLICATE_EVENT_' + key, {
					url: window.location.pathname + window.location.hash
				});
			}
		});
	}

	return duplicatesFound;
}

function flushQueue() {
	let userContext = model.get('userContext');
	let offlineMode = model.get('offlineMode');

	if (queue.length === 0) {
		return;
	}

	if ((offlineMode && settingOfflineEvents) || typeof offlineMode === 'undefined') {
		//Ensure we don't get('offlineEvents')
		//  as it's being set to avoid dropped events
		// 	set timer to try again in 100ms
		timerId = setTimeout(flushQueue, 100);
		return;
	} else if (offlineAvailable && offlineMode && userContext && userContext.uid) {
		let uid = userContext.uid;
		settingOfflineEvents = true;
		get('offlineEvents')
			.then(offlineEventData => {
				queue = queue.map(event => {
					event.info.offline = true;
					return event;
				});

				offlineEventData = offlineEventData || {};
				let offlineEvents = offlineEventData[uid] || [];
				offlineEvents = offlineEvents.concat(queue);

				timerId = null;
				queue = [];
				tracker = {};

				offlineEventData = _.assign({}, offlineEventData, {
					[uid]: offlineEvents
				});

				return set('offlineEvents', offlineEventData).finally(() => {
					settingOfflineEvents = false;
					resolveDeferred();
				});
			})
			.catch(resolveDeferred);
	} else {
		checkForDuplicates();
		analyticsService
			.events({
				events: queue
			})
			.then(resolveDeferred)
			.catch(resolveDeferred);

		timerId = null;
		queue = [];
		tracker = {};
	}
}

const event = (code, info) => {
	let tokenizedInfo = JSON.stringify(info);

	if (timerId !== null) {
		clearTimeout(timerId);
	}

	if (tracker[code] && tracker[code][tokenizedInfo]) {
		tracker[code][tokenizedInfo] += 1;
	} else if (tracker[code]) {
		tracker[code][tokenizedInfo] = 1;
	} else {
		tracker[code] = {
			[tokenizedInfo]: 1
		};
	}

	// User clock can be set to really odd things
	return getTimestamp().then(timestamp => {
		timestamp = timestamp || Date.now();
		let ts = new Date(timestamp).toISOString();
		info['@timestamp'] = ts;
		let data = {
			event: code,
			info: info
		};
		queue.push(data);
		timerId = setTimeout(flushQueue, 0);

		deferred = deferred || Q.defer();
		return deferred.promise;
	});
};

const uploadOfflineEvents = () => {
	let userContext = model.get('userContext');
	settingOfflineEvents = true;

	if (!offlineAvailable) {
		return Q.reject();
	}
	if (!userContext || !userContext.uid) {
		return Q.reject('User needs to be logged in.');
	}
	let uid = userContext.uid;
	return get('offlineEvents').then(offlineEventData => {
		offlineEventData = offlineEventData || {};
		let offlineEvents = offlineEventData[uid] || [];
		let numOfEvents = (offlineEvents || []).length;
		let prms = Q();
		if (numOfEvents) {
			let eventsToUpload = [...offlineEvents];
			let events = eventsToUpload.splice(0, 30);
			prms = prms
				.then(() => {
					offlineEventData = _.assign({}, offlineEventData, {
						[uid]: eventsToUpload
					});
					return set('offlineEvents', offlineEventData);
				})
				.then(() =>
					analyticsService.events({
						events
					})
				)
				.then(() => {
					return uploadOfflineEvents();
				})
				.finally(() => (settingOfflineEvents = false));
		}

		return prms.finally(() => (settingOfflineEvents = false));
	});
};

if (offlineAvailable) {
	get('offlineMode')
		.catch(e => {})
		.finally(() => getTimestamp());
} else {
	getTimestamp();
}

model.change('idbWriteable', writeable => {
	if (writeable === false) {
		offlineAvailable = false;
	}
});

module.exports = {
	event,
	error,
	uploadOfflineEvents
};
